import React from 'react';
import { MicroTrend } from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/useMicroTrendsListData';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import ScrollableContainer from 'src/components/BeaconV2Components/ScrollableContainer';

import {
  SlDialog,
  SlDialogCloseButton,
  SlDialogContent,
  SlDialogFooter,
  SlDialogHeader,
  SlDialogRoot,
  SlDialogTitle
} from 'src/components/BeaconV2Components/SlDialog';
import { useEntityMetadataEnhancer } from 'src/components/AtlasV2Components/Hooks/useEntityMetadataEnhancer';
import colors from 'src/utils/colors';

export const MicroTrendsDetailModal = ({
  open,
  onClose,
  trendData,
  parentTrends,
  onView,
  skuCount
}: {
  open: boolean;
  onClose: () => void;
  trendData: MicroTrend;
  currentIndex?: number;
  totalTrends?: number;
  onNavigate?: (direction: 'prev' | 'next') => void;
  onView?: () => void;
  parentTrends: MicroTrend[];
  skuCount: number;
}) => {
  const { getEntityDisplayName } = useEntityMetadataEnhancer();

  // Get category name for display
  const categoryName = getEntityDisplayName(trendData?.subcategoryId, 'subcategoryId');

  if (!trendData) return null;
  // Create display name based on trend type
  const displayName =
    trendData?.type === 1
      ? `${trendData.brandName} - ${categoryName}`
      : `${trendData.tagName} (${trendData.tagValue}) - ${categoryName}`;

  return (
    <SlDialogRoot>
      <SlDialog onClose={onClose} open={open} width={640} height={545}>
        <SlDialogHeader>
          <SlDialogTitle>Microtrend Details</SlDialogTitle>
          <SlDialogCloseButton onClick={onClose} />
        </SlDialogHeader>

        <ScrollableContainer
          scrollbarWidth="5px"
          thumbColor={colors.blueGrey}
          sx={{ overflowY: 'auto', height: '100%', flexGrow: 1, minHeight: '400px' }}
        >
          <SlDialogContent sx={{ overflowX: 'hidden', padding: '24px 48px' }}>
            {/* Product Name */}
            <Text variant="h6" fontWeight="500" sx={{ marginBottom: '24px' }}>
              {displayName}
            </Text>

            {/* Two Column Layout */}
            <Flex gap={48} sx={{ marginBottom: '16px' }}>
              {/* Left Column */}
              <Flex flexDirection="row" flex={1} gap={24}>
                {/* Classification */}
                <Flex flexDirection="column" gap={8} minWidth="272px">
                  <Text variant="body2" sx={{ lineHeight: '20px', letterSpacing: '0.08px', fontWeight: 500 }}>
                    Classification
                  </Text>

                  <Text variant="body2" sx={{ lineHeight: '20px', letterSpacing: '0.08px' }}>
                    {parentTrends.map((trend, index) =>
                      index === 0 ? `${trend.numWeeks} Weeks ` : ` / ${trend.numWeeks} Weeks `
                    )}
                  </Text>
                </Flex>
                <Flex flexDirection="column" gap={24} sx={{ minWidth: '150px' }}>
                  {/* Product Count */}
                  <Flex flexDirection="column" gap={8}>
                    <Text variant="body2" sx={{ lineHeight: '20px', letterSpacing: '0.08px', fontWeight: 500 }}>
                      Product Count
                    </Text>
                    <Text variant="body2" sx={{ lineHeight: '20px', letterSpacing: '0.08px' }}>
                      {skuCount}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            {/* Description */}
            <Flex flexDirection="column" gap={8}>
              <Text variant="body2" sx={{ lineHeight: '20px', letterSpacing: '0.08px', fontWeight: 500 }}>
                Description
              </Text>
              <Text variant="body2" sx={{ lineHeight: '20px', letterSpacing: '0.08px' }}>
                {trendData?.summary}
              </Text>
            </Flex>
          </SlDialogContent>
        </ScrollableContainer>

        <SlDialogFooter
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 48px'
          }}
        >
          {/* Left side - Cancel button */}
          <SlButton onClick={onClose}>Cancel</SlButton>

          {/* Right side - View button */}
          <SlButton variant="contained" onClick={onView}>
            View
          </SlButton>
        </SlDialogFooter>
      </SlDialog>
    </SlDialogRoot>
  );
};
